import React, { useEffect, useState } from "react";
import { ChevronRight, Star, HelpCircle, Quote, Smartphone, ShieldCheck, Banknote, Zap, ArrowRight, SmartphoneIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";

interface Brand { id: number; name: string; image: string; }
interface Slider { id: number; image: string; }
interface Feedback { id: number; name: string; feedback: string; rating: number; city?: string; }

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [sliders, setSliders] = useState<Slider[]>([]);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlider, setCurrentSlider] = useState(0);

  // LOGIC UNTOUCHED
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sliderRes, brandRes, feedbackRes] = await Promise.all([
          api.get("/sliders"),
          api.get("/brands/popular"),
          api.get("/feedbacks")
        ]);
        setSliders(sliderRes || []);
        setBrands(brandRes || []);
        setFeedbacks(feedbackRes || []);
      } catch (err) {
        console.error("Data fetching failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (sliders.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlider((prev) => (prev + 1) % sliders.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [sliders]);

  return (
    <div className="pb-10 bg-[#F8FAFC] font-sans antialiased text-slate-900 relative min-h-screen">
      
      {/* Ambient Premium Background Glow */}
      <div className="absolute top-0 inset-x-0 h-96 bg-gradient-to-b from-[#363C90]/5 to-transparent pointer-events-none -z-10" />

      {/* --- 1. HERO SLIDER --- */}
      <section className="pt-6 pb-8">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="relative overflow-hidden rounded-[24px] aspect-[2/1] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 group">
            {sliders.map((slider, index) => (
              <div
                key={slider.id}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlider ? "opacity-100" : "opacity-0"}`}
              >
                <img 
                  src={slider.image} 
                  alt="Promo" 
                  className="w-full h-full object-cover transform transition-transform duration-10000 scale-105" 
                />
                {/* Premium Gradient Overlay for better dot visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-80" />
              </div>
            ))}

            {/* Pagination Dots */}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {sliders.map((_, i) => (
                <button 
                  key={i} 
                  onClick={() => setCurrentSlider(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${i === currentSlider ? "w-8 bg-white shadow-[0_0_10px_rgba(255,255,255,0.8)]" : "w-2 bg-white/50 hover:bg-white/80"}`} 
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- 2. TRUST BADGES (Redesigned as Floating Cards) --- */}
      <section className="pb-10 relative z-10">
        <div className="max-w-[1200px] mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
                { icon: <Zap size={20} />, label: "Instant Cash", sub: "Spot Payment", color: "text-amber-500", bg: "bg-amber-50" },
                { icon: <ShieldCheck size={20} />, label: "Data Safety", sub: "100% Secure", color: "text-blue-500", bg: "bg-blue-50" },
                { icon: <SmartphoneIcon size={20} />, label: "Best Price", sub: "Guaranteed", color: "text-[#21C2A5]", bg: "bg-[#21C2A5]/10" },
                { icon: <Banknote size={20} />, label: "Free Pickup", sub: "At Doorstep", color: "text-purple-500", bg: "bg-purple-50" }
            ].map((item, i) => (
                <div key={i} className="flex flex-col md:flex-row items-center md:items-start gap-3 justify-center md:justify-start bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-300 group">
                    <div className={`${item.color} ${item.bg} w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      {item.icon}
                    </div>
                    <div className="text-center md:text-left mt-1 md:mt-0">
                        <p className="text-[14px] font-bold text-slate-800 leading-tight">{item.label}</p>
                        <p className="text-[11px] text-slate-500 font-medium mt-0.5">{item.sub}</p>
                    </div>
                </div>
            ))}
        </div>
      </section>

      {/* --- 3. PREMIUM BRAND GRID --- */}
      <section className="py-8">
        <div className="max-w-[1200px] mx-auto px-4">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">Sell Your Device</h2>
              <p className="text-sm text-slate-500 mt-1">Select your brand to get an instant quote</p>
            </div>
            <button 
              onClick={() => navigate('/sell/brands')} 
              className="text-[#363C90] text-[13px] font-bold flex items-center gap-1.5 hover:text-[#21C2A5] transition-colors group"
            >
              View All <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
            {loading ? [...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-white rounded-[20px] animate-pulse border border-slate-100 shadow-sm" />
            )) : brands.map((brand) => (
              <div 
                key={brand.id} 
                onClick={() => navigate(`/sell/products/${brand.id}?brand=${brand.name}`)}
                className="bg-white border border-slate-100 p-5 rounded-[20px] flex flex-col items-center justify-center gap-3 hover:border-[#363C90]/30 hover:shadow-[0_8px_25px_rgba(54,60,144,0.12)] hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
              >
                <div className="h-10 w-full flex items-center justify-center">
                    <img src={brand.image} alt={brand.name} className="max-h-full object-contain group-hover:scale-110 transition-transform duration-300" />
                </div>
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide group-hover:text-[#363C90] transition-colors">{brand.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 4. SLEEK TESTIMONIALS --- */}
      {feedbacks.length > 0 && (
        <section className="py-16 mt-8 relative">
          <div className="absolute inset-0 bg-[#363C90]/[0.02] skew-y-[-2deg] transform-origin-top-left -z-10" />
          <div className="max-w-[1200px] mx-auto px-4 relative z-10">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">Trusted by Thousands</h2>
              <p className="text-slate-500 text-sm mt-2">See what our customers have to say</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {feedbacks.map((item) => (
                <div key={item.id} className="bg-white p-7 rounded-[24px] border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-lg transition-shadow relative overflow-hidden group">
                  {/* Decorative Background Quote */}
                  <Quote size={80} className="absolute -top-4 -right-4 text-slate-50 opacity-50 group-hover:scale-110 transition-transform duration-500" />
                  
                  <div className="flex gap-1 mb-4 relative z-10">
                    {[...Array(item.rating || 5)].map((_, j) => <Star key={j} size={14} className="fill-amber-400 text-amber-400" />)}
                  </div>
                  <p className="text-slate-700 text-[14px] leading-relaxed mb-6 relative z-10 italic">"{item.feedback}"</p>
                  
                  <div className="flex items-center gap-4 pt-5 border-t border-slate-100/80 relative z-10">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#363C90] to-[#21C2A5] flex items-center justify-center text-white font-bold text-sm shadow-md">
                      {item.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-[14px] font-bold text-slate-900">{item.name}</h4>
                      <p className="text-[11px] text-slate-400 uppercase font-bold tracking-wider">{item.city || "Verified Seller"}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* --- 5. PREMIUM CTA SECTION (Untouched from previous step) --- */}
      <section className="py-16 px-4">
        <div className="max-w-[1000px] mx-auto bg-gradient-to-r from-[#1E1B4B] to-[#363C90] rounded-[32px] p-10 md:p-14 text-center shadow-2xl relative overflow-hidden border border-indigo-900/50">
          
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
            <div className="absolute -top-32 -left-32 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -right-32 w-80 h-80 bg-[#21C2A5]/20 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 flex flex-col items-center">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
              Selling is now <span className="text-[#21C2A5]">Easier.</span>
            </h2>
            
            <p className="text-indigo-100/80 text-base md:text-lg mb-8 max-w-md mx-auto">
              Download the Sell30 app today for exclusive price bumps, instant quotes, and a seamless experience.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center items-center">
              <a 
                href="https://play.google.com/store/apps/details?id=com.sell30user.app&hl=en_IN" 
                target="_blank" 
                rel="noopener noreferrer"
                className="transform hover:scale-105 transition-all duration-300 drop-shadow-xl hover:drop-shadow-2xl"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                  alt="Get it on Google Play" 
                  className="h-12 md:h-14" 
                />
              </a>

              <div 
                className="transform hover:scale-105 transition-all duration-300 drop-shadow-xl opacity-70 hover:opacity-100 cursor-pointer"
                title="Coming soon for iOS"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                  alt="Download on the App Store" 
                  className="h-12 md:h-14" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomeScreen;